import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Upload, Plus, X } from 'lucide-react';

const CouncillorProfileEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [councillor, setCouncillor] = useState(null);
  
  const [formData, setFormData] = useState({
    profile_photo_url: '',
    biography: '',
    priorities: [],
    office_hours: '',
    social_links: {
      twitter: '',
      facebook: '',
      instagram: '',
      website: '',
    },
  });

  const [newPriority, setNewPriority] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/councillors/my-profile');
      const data = response.data;
      
      setCouncillor(data.councillor);
      setProfile(data.profile);
      
      // Parse JSON fields
      let priorities = [];
      let socialLinks = { twitter: '', facebook: '', instagram: '', website: '' };
      
      try {
        if (data.profile?.priorities) {
          priorities = JSON.parse(data.profile.priorities);
        }
      } catch (e) {
        priorities = [];
      }
      
      try {
        if (data.profile?.social_links) {
          socialLinks = { ...socialLinks, ...JSON.parse(data.profile.social_links) };
        }
      } catch (e) {
        // Keep defaults
      }
      
      setFormData({
        profile_photo_url: data.profile?.profile_photo_url || data.councillor?.image_url || '',
        biography: data.profile?.biography || data.councillor?.bio || '',
        priorities: priorities,
        office_hours: data.profile?.office_hours || '',
        social_links: socialLinks,
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 403) {
        toast.error('You must be a verified councillor to access this page');
        navigate('/');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/councillors/my-profile', {
        profile_photo_url: formData.profile_photo_url || null,
        biography: formData.biography || null,
        priorities: JSON.stringify(formData.priorities),
        office_hours: formData.office_hours || null,
        social_links: JSON.stringify(formData.social_links),
      });
      
      toast.success('Profile updated successfully!');
      navigate('/councillor/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addPriority = () => {
    if (newPriority.trim() && formData.priorities.length < 5) {
      setFormData({
        ...formData,
        priorities: [...formData.priorities, newPriority.trim()],
      });
      setNewPriority('');
    }
  };

  const removePriority = (index) => {
    setFormData({
      ...formData,
      priorities: formData.priorities.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <div className="text-white font-mono">LOADING PROFILE...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/councillor/dashboard')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold font-mono">
                    EDIT <span className="text-green-500">PROFILE</span>
                  </h1>
                  <p className="text-gray-400 text-sm mt-1">
                    {councillor?.name} • {councillor?.ward}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-black font-bold px-6 py-2 rounded transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
          {/* Profile Photo */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Profile Photo</h2>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gray-800 overflow-hidden">
                {formData.profile_photo_url ? (
                  <img 
                    src={formData.profile_photo_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600">
                    <Upload className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="url"
                  value={formData.profile_photo_url}
                  onChange={(e) => setFormData({ ...formData, profile_photo_url: e.target.value })}
                  placeholder="Enter image URL..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Enter a URL for your profile photo. Recommended: square image, at least 200x200px.
                </p>
              </div>
            </div>
          </div>

          {/* Biography */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Biography</h2>
            <textarea
              value={formData.biography}
              onChange={(e) => setFormData({ ...formData, biography: e.target.value })}
              placeholder="Tell your constituents about yourself, your background, and what you're working on..."
              rows={6}
              className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:outline-none focus:border-green-500 resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              {formData.biography.length}/1000 characters
            </p>
          </div>

          {/* Priorities */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Your Priorities</h2>
            <p className="text-gray-400 text-sm mb-4">
              List up to 5 key priorities you're focusing on for your ward.
            </p>
            
            <div className="space-y-3 mb-4">
              {formData.priorities.map((priority, index) => (
                <div key={index} className="flex items-center gap-3 bg-gray-800 border border-gray-700 rounded px-4 py-2">
                  <span className="text-green-500 font-mono">{index + 1}.</span>
                  <span className="flex-1">{priority}</span>
                  <button
                    onClick={() => removePriority(index)}
                    className="text-gray-500 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {formData.priorities.length < 5 && (
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addPriority()}
                  placeholder="Add a priority..."
                  className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
                <button
                  onClick={addPriority}
                  disabled={!newPriority.trim()}
                  className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-4 py-2 rounded transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            )}
          </div>

          {/* Office Hours */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Office Hours</h2>
            <textarea
              value={formData.office_hours}
              onChange={(e) => setFormData({ ...formData, office_hours: e.target.value })}
              placeholder="e.g., Mondays 9am-12pm at City Hall, or by appointment..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:outline-none focus:border-green-500 resize-none"
            />
          </div>

          {/* Social Links */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Social Media & Website</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Twitter/X</label>
                <input
                  type="url"
                  value={formData.social_links.twitter}
                  onChange={(e) => setFormData({
                    ...formData,
                    social_links: { ...formData.social_links, twitter: e.target.value }
                  })}
                  placeholder="https://twitter.com/..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Facebook</label>
                <input
                  type="url"
                  value={formData.social_links.facebook}
                  onChange={(e) => setFormData({
                    ...formData,
                    social_links: { ...formData.social_links, facebook: e.target.value }
                  })}
                  placeholder="https://facebook.com/..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Instagram</label>
                <input
                  type="url"
                  value={formData.social_links.instagram}
                  onChange={(e) => setFormData({
                    ...formData,
                    social_links: { ...formData.social_links, instagram: e.target.value }
                  })}
                  placeholder="https://instagram.com/..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Website</label>
                <input
                  type="url"
                  value={formData.social_links.website}
                  onChange={(e) => setFormData({
                    ...formData,
                    social_links: { ...formData.social_links, website: e.target.value }
                  })}
                  placeholder="https://..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm focus:outline-none focus:border-green-500"
                />
              </div>
            </div>
          </div>

          {/* Read-only Info Notice */}
          <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
            <p className="text-yellow-500 text-sm font-mono">
              ℹ️ Your name, ward assignment, voting records, and responsiveness metrics are managed by CYQR administrators and cannot be edited here. If you believe there's an error, please use the "Report Error" feature in your dashboard.
            </p>
          </div>

          {/* Save Button (bottom) */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/councillor/dashboard')}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-black font-bold px-6 py-3 rounded transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CouncillorProfileEdit;
